import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts'
import type { PositionSnapshot } from '@/lib/repositories'

interface PnlChartProps {
  snapshots: PositionSnapshot[]
}

interface AggregatedDay {
  snapshotDate: string
  totalPnl: number
  totalPnlPercent: number
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{ value: number; payload: AggregatedDay }>
  label?: string
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length || !payload[0]) return null

  const entry = payload[0]
  const day = entry.payload
  const value = entry.value
  const isPositive = value >= 0

  return (
    <div className="rounded-lg bg-neutral-900 border border-white/[0.06] px-3 py-2 shadow-lg">
      <p className="text-xs text-neutral-400 mb-1">
        {format(parseISO(day.snapshotDate), 'MMM d, yyyy')}
      </p>
      <p
        className={`text-sm font-mono font-medium ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}
      >
        {isPositive ? '+' : ''}
        {formatCurrency(value)}
      </p>
      <p
        className={`text-xs font-mono ${isPositive ? 'text-emerald-400/70' : 'text-red-400/70'}`}
      >
        {isPositive ? '+' : ''}
        {day.totalPnlPercent.toFixed(2)}%
      </p>
    </div>
  )
}

export function PnlChart({ snapshots }: PnlChartProps) {
  const { t } = useTranslation()

  // Aggregate per-position snapshots into daily totals
  const aggregated = useMemo(() => {
    const byDate = new Map<
      string,
      { totalPnl: number; totalBasis: number }
    >()

    for (const snap of snapshots) {
      const existing = byDate.get(snap.snapshotDate)
      if (existing) {
        existing.totalPnl += snap.pnl
        existing.totalBasis += snap.buyInPrice
      } else {
        byDate.set(snap.snapshotDate, {
          totalPnl: snap.pnl,
          totalBasis: snap.buyInPrice,
        })
      }
    }

    const days: AggregatedDay[] = []
    for (const [date, { totalPnl, totalBasis }] of byDate) {
      days.push({
        snapshotDate: date,
        totalPnl: Math.round(totalPnl * 100) / 100,
        totalPnlPercent:
          totalBasis > 0
            ? Math.round((totalPnl / totalBasis) * 100 * 100) / 100
            : 0,
      })
    }

    return days.sort((a, b) => a.snapshotDate.localeCompare(b.snapshotDate))
  }, [snapshots])

  if (aggregated.length < 2) {
    return (
      <div className="flex items-center justify-center h-[120px] text-neutral-500 text-xs">
        {t('notEnoughChartData')}
      </div>
    )
  }

  const latestPnl = aggregated[aggregated.length - 1]!.totalPnl
  const isPositive = latestPnl >= 0
  const strokeColor = isPositive ? '#10b981' : '#ef4444'
  const fillId = isPositive ? 'pnlGradientGreen' : 'pnlGradientRed'

  return (
    <div className="w-full h-[200px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={aggregated}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="pnlGradientGreen" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="pnlGradientRed" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="snapshotDate"
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#737373', fontSize: 11 }}
            tickFormatter={(val: string) => format(parseISO(val), 'MMM d')}
            interval="preserveStartEnd"
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#737373', fontSize: 11 }}
            tickFormatter={(val: number) => formatCurrency(val)}
            width={65}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="totalPnl"
            stroke={strokeColor}
            strokeWidth={2}
            fill={`url(#${fillId})`}
            dot={false}
            activeDot={{
              r: 4,
              fill: strokeColor,
              stroke: '#0a0a0a',
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
